import { useReducer } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { downloadSentOtcInvoice } from "@/lib/api/otcInvoices";
import { downloadFromS3 } from "@/lib/file";
import {
  DownloadSentOtcInvoiceRequestValidation,
  type DownloadSentOtcInvoiceRequest,
} from "@/lib/validation/otcInvoices";

type DocumentType = DownloadSentOtcInvoiceRequest["document_type"];

type DownloadState = {
  isOpen: boolean;
  status: "idle" | "generating" | "success" | "error";
  documentType: DocumentType | null;
  url: string | null;
  error: string | null;
};

type DownloadAction =
  | { type: "open" }
  | { type: "generate"; documentType: DocumentType }
  | { type: "success"; url: string }
  | { type: "error"; error: string }
  | { type: "close" };

const initialDownloadState: DownloadState = {
  isOpen: false,
  status: "idle",
  documentType: null,
  url: null,
  error: null,
};

function downloadReducer(state: DownloadState, action: DownloadAction): DownloadState {
  switch (action.type) {
    case "open":
      return { ...initialDownloadState, isOpen: true };
    case "generate":
      return {
        isOpen: true,
        status: "generating",
        documentType: action.documentType,
        url: null,
        error: null,
      };
    case "success":
      return { ...state, status: "success", url: action.url, error: null };
    case "error":
      return { ...state, status: "error", url: null, error: action.error };
    case "close":
      return initialDownloadState;
  }
}

function documentTypeLabel(documentType: DocumentType | null): string {
  return documentType === "pdf" ? "PDF" : "Excel";
}

function errorDescription(result: { message?: string; data?: unknown }): string {
  const context = result.data === undefined ? "" : ` ${JSON.stringify(result.data)}`;
  return `${result.message || "The request failed."}${context}`;
}

export function DownloadOtcInvoiceButton({ invoiceId }: { invoiceId: string }) {
  const [state, dispatch] = useReducer(downloadReducer, initialDownloadState);

  const runDownload = async (documentType: DocumentType): Promise<void> => {
    const parsed = DownloadSentOtcInvoiceRequestValidation.safeParse({
      invoice_id: invoiceId,
      document_type: documentType,
    });
    if (!parsed.success || state.status === "generating") return;

    dispatch({ type: "generate", documentType });
    try {
      const result = await downloadSentOtcInvoice(parsed.data);
      if (!result.success) {
        dispatch({ type: "error", error: errorDescription(result) });
        return;
      }

      const downloadResult = await downloadFromS3(
        result.data.presigned_get_url,
        result.data.filename,
      );
      if (!downloadResult.success) {
        dispatch({ type: "error", error: "Failed to download the OTC invoice file." });
        return;
      }

      dispatch({ type: "success", url: result.data.presigned_get_url });
    } catch (error: unknown) {
      dispatch({
        type: "error",
        error: error instanceof Error ? error.message : "The request failed.",
      });
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => dispatch({ type: "open" })}>
        <Download className="mr-2 h-4 w-4" /> Download Invoice
      </Button>
      <Dialog open={state.isOpen} onOpenChange={(open: boolean) => { if (!open) dispatch({ type: "close" }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download OTC Invoice</DialogTitle>
            <DialogDescription>
              {state.status === "idle"
                ? "Select the invoice file type to download."
                : state.status === "generating"
                  ? `Generating your invoice ${documentTypeLabel(state.documentType)} download...`
                  : state.status === "error"
                    ? `The invoice ${documentTypeLabel(state.documentType)} download could not be generated.`
                    : `Your invoice ${documentTypeLabel(state.documentType)} download is ready.`}
            </DialogDescription>
          </DialogHeader>
          {state.error ? <p className="text-sm text-destructive">{state.error}</p> : null}
          {state.url ? (
            <a href={state.url} download className="text-sm text-blue-600 underline">
              Download invoice {documentTypeLabel(state.documentType)}
            </a>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={state.status === "generating"}
              onClick={() => runDownload("excel")}
            >
              Generate Excel Download
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={state.status === "generating"}
              onClick={() => runDownload("pdf")}
            >
              Generate PDF Download
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => dispatch({ type: "close" })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
